import apiClient from '../client';
import type {
  KanbanBoard,
  UpdateBucketRequest,
  UpdateBucketResponse,
} from '../types/kanban.types';

/**
 * Kanban Board API Service
 */
export const kanbanService = {
  /**
   * Get full board data including buckets and items
   * @param slug - Board slug (e.g., 'razvoj', 'biznisoft_mobile')
   * @returns Full board data with all buckets and items
   */
  async getBoardBySlug(slug: string): Promise<KanbanBoard> {
    const response = await apiClient.get<KanbanBoard>(
      `/kanban/boards/slug/${slug}/full`
    );
    return response.data;
  },

  /**
   * Update ticket bucket (move ticket to different column)
   * @param ticketId - Ticket ID
   * @param bucketId - Target bucket ID
   * @returns Update response
   */
  async updateTicketBucket(
    ticketId: string,
    bucketId: string
  ): Promise<UpdateBucketResponse> {
    const response = await apiClient.patch<UpdateBucketResponse>(
      `/kanban/tickets/${ticketId}/bucket`,
      { bucket_id: bucketId } as UpdateBucketRequest
    );
    return response.data;
  },

  /**
   * Reorder bucket within a board
   * @param bucketId - Bucket ID to reorder
   * @param newSortOrder - New sort order position
   * @returns Update response
   */
  async reorderBucket(
    bucketId: string,
    newSortOrder: number
  ): Promise<{ success: boolean }> {
    const response = await apiClient.post<{ success: boolean }>(
      `/kanban/buckets/id/${bucketId}/reorder`,
      { new_sort_order: newSortOrder }
    );
    return response.data;
  },

  /**
   * Move item to different bucket
   * @param itemId - Item ID to move
   * @param targetBucketId - Target bucket ID
   * @param newSortOrder - Optional new sort order in target bucket
   * @returns Update response
   */
  async moveItem(
    itemId: string,
    targetBucketId: string,
    newSortOrder?: number
  ): Promise<{ success: boolean }> {
    const response = await apiClient.post<{ success: boolean }>(
      `/kanban/items/id/${itemId}/move`,
      {
        target_bucket_id: targetBucketId,
        new_sort_order: newSortOrder,
      }
    );
    return response.data;
  },

  /**
   * Reorder item within its bucket
   * @param itemId - Item ID to reorder
   * @param newSortOrder - New sort order position
   * @returns Update response
   */
  async reorderItem(
    itemId: string,
    newSortOrder: number
  ): Promise<{ success: boolean }> {
    const response = await apiClient.post<{ success: boolean }>(
      `/kanban/items/id/${itemId}/reorder`,
      { new_sort_order: newSortOrder }
    );
    return response.data;
  },
};
